/**
 * Project Setup → Knowledge Hub (Knowledge_Hub_Design.md sections 4.3, 12 Phase 1d).
 *
 * Three parts:
 *  - **Executable knowledge** — the engagement and DQ packs this project's runs
 *    evaluate. Data Steward (project rule role) or platform admin only; audited.
 *    It IS project.config.engagement_slug / dq_rule_packs, so a change applies
 *    at the next profiling run.
 *  - **Context knowledge** — every document this project can see (its own and
 *    tenant-shared), with an include/exclude per agent. Phase 1: recorded and
 *    audited; each agent starts reading through the hub in Phase 2, which the
 *    column header says.
 *  - **Agent budgets** and the ZTD-provided reference packs (read-only).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Loader2,
  Lock,
  Save,
  Share2,
} from "lucide-react";
import {
  knowledgeHub,
  STORE_LABELS,
  VISIBILITY_LABELS,
  type ContextItem,
  type ProjectKnowledgeView,
  type SelectionMode,
} from "../../api/knowledgeHub";
import PromoteDocumentDialog from "./PromoteDocumentDialog";

interface Props {
  projectId: string;
}

const PANEL_STYLE = {
  background: "rgba(28,28,42,0.5)",
  border: "1px solid rgba(255,255,255,0.05)",
};

export default function ProjectKnowledgeHub({ projectId }: Props) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<ProjectKnowledgeView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [promoting, setPromoting] = useState<ContextItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setView(await knowledgeHub.projectView(projectId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load knowledge");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open && !view) void load();
  }, [open, view, load]);

  const flash = (message: string) => {
    setNotice(message);
    setTimeout(() => setNotice(""), 3000);
  };

  return (
    <div
      className="glass rounded-2xl overflow-hidden"
      data-testid="knowledge-hub-section"
      style={PANEL_STYLE}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/[0.02] transition"
        data-testid="knowledge-hub-toggle"
      >
        <BookOpen size={16} className="text-teal-400" />
        <span className="text-sm font-bold text-white flex-1">
          Knowledge Hub
        </span>
        {view && (
          <span className="text-[11px] text-white/40">
            {view.context_items.length} documents ·{" "}
            {view.executable.packs.filter((p) => p.selected).length} packs
            active
          </span>
        )}
        {open ? (
          <ChevronUp size={14} className="text-white/30" />
        ) : (
          <ChevronDown size={14} className="text-white/30" />
        )}
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 space-y-6">
          {loading && !view && (
            <div className="flex items-center justify-center py-4">
              <Loader2 size={20} className="animate-spin text-white/20" />
            </div>
          )}
          {error && (
            <div
              className="flex items-center gap-2 text-xs text-red-400"
              data-testid="knowledge-hub-error"
            >
              <AlertTriangle size={13} /> {error}
            </div>
          )}
          {notice && (
            <div
              className="text-xs text-green-400"
              data-testid="knowledge-hub-notice"
            >
              {notice}
            </div>
          )}
          {view && (
            <>
              {view.unavailable_sources.length > 0 && (
                <div className="text-xs text-amber-400 flex items-center gap-2">
                  <AlertTriangle size={13} />
                  Could not list:{" "}
                  {view.unavailable_sources.map((s) => s.title).join(", ")}
                </div>
              )}
              <ExecutableSection
                view={view}
                projectId={projectId}
                onSaved={async (message) => {
                  flash(message);
                  await load();
                }}
                onError={setError}
              />
              <ContextSection
                view={view}
                projectId={projectId}
                onChanged={load}
                onError={setError}
                onPromote={setPromoting}
              />
              <BudgetSection
                view={view}
                projectId={projectId}
                onSaved={async () => {
                  flash("Budget saved");
                  await load();
                }}
                onError={setError}
              />
              <ReferencePacks view={view} />
            </>
          )}
        </div>
      )}
      {promoting && (
        <PromoteDocumentDialog
          item={promoting}
          onClose={() => setPromoting(null)}
          onPromoted={(status) => {
            setPromoting(null);
            flash(
              status === "ready"
                ? "Copied to tenant-shared knowledge"
                : status === "pending_classification_review"
                  ? "Copied, but held for Tier-3 classification review: an administrator must clear it"
                  : `Copied, but processing ended as "${status}"`,
            );
            void load();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Executable knowledge (DQ packs) — steward-only
// ---------------------------------------------------------------------------

function ExecutableSection({
  view,
  projectId,
  onSaved,
  onError,
}: {
  view: ProjectKnowledgeView;
  projectId: string;
  onSaved: (message: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  const { binding, engagements } = view.executable;
  const canEdit = view.permissions.can_select_executable;
  const [slug, setSlug] = useState(binding.engagement_slug ?? "");
  const [packs, setPacks] = useState<Set<string>>(
    new Set(binding.dq_rule_packs ?? []),
  );
  const [pinned, setPinned] = useState(binding.binding === "pinned");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  // Re-sync with what is stored after every reload (a save here, or one made elsewhere).
  useEffect(() => {
    setSlug(binding.engagement_slug ?? "");
    setPacks(new Set(binding.dq_rule_packs ?? []));
    setPinned(binding.binding === "pinned");
  }, [binding.engagement_slug, binding.dq_rule_packs, binding.binding]);

  const available = useMemo(
    () => engagements.find((e) => e.slug === slug)?.packs ?? [],
    [engagements, slug],
  );
  // Stored pack names this engagement does not have (set through another route, or a pack removed from disk). They are
  // listed so they can be unticked; otherwise they would be sent, invisibly, on every save.
  const unknownPacks = useMemo(
    () => Array.from(packs).filter((p) => !available.includes(p)),
    [packs, available],
  );
  const rulesByPack = useMemo(() => {
    const out: Record<string, number> = {};
    for (const p of view.executable.packs) {
      if (p.details.engagement_slug === slug)
        out[String(p.details.pack)] = Number(p.details.rule_count ?? 0);
    }
    return out;
  }, [view.executable.packs, slug]);

  const save = async () => {
    setSaving(true);
    try {
      await knowledgeHub.setExecutable(projectId, {
        engagement_slug: slug || null,
        // No pack ticked = every pack under the engagement (the key is removed).
        dq_rule_packs: packs.size ? Array.from(packs) : null,
        binding: pinned ? "pinned" : "auto",
        reason: reason || undefined,
      });
      setReason("");
      await onSaved("Executable knowledge saved — applies at the next run");
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="knowledge-executable">
      <h4 className="text-xs font-bold text-white/70 uppercase tracking-wide mb-1 flex items-center gap-2">
        Executable knowledge — DQ rule packs
        {!canEdit && <Lock size={11} className="text-white/30" />}
      </h4>
      <p className="text-xs text-white/40 mb-3">
        Changes what profiling runs evaluate and the quality score. Data Steward
        or platform admin only; every change is audited.
        {!canEdit && " You can view this; ask a Data Steward to change it."}
      </p>
      {binding.dq_rule_packs !== null && binding.dq_rule_packs.length === 0 && (
        <Warning testId="knowledge-no-packs-warning">
          This project&apos;s stored pack list is empty, so its runs evaluate no
          pack rules (the Rules tab lists every pack for it). Saving here stores
          &quot;every pack&quot; unless you tick specific packs.
        </Warning>
      )}
      {!binding.pack_filter_valid && (
        <Warning testId="knowledge-invalid-packs-warning">
          This project&apos;s stored pack list is not a list of pack names;
          saving here replaces it.
        </Warning>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-white/40">
          Engagement
          <select
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setPacks(new Set());
            }}
            disabled={!canEdit}
            data-testid="knowledge-engagement-select"
            className="mt-1 w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white disabled:opacity-50"
          >
            <option value="">— None —</option>
            {engagements.map((e) => (
              <option key={e.slug} value={e.slug}>
                {e.slug}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-white/60 mt-5">
          <input
            type="checkbox"
            checked={pinned}
            onChange={(e) => setPinned(e.target.checked)}
            disabled={!canEdit}
            data-testid="knowledge-binding-pinned"
            className="accent-brand-500"
          />
          Pin this engagement (connecting a source no longer re-derives it)
        </label>
      </div>
      {slug && (
        <div className="mt-3 space-y-1.5" data-testid="knowledge-packs">
          <div className="text-xs text-white/40">
            Packs {packs.size === 0 && "— every pack runs when none is ticked"}
          </div>
          {[...available, ...unknownPacks].map((name) => (
            <label
              key={name}
              className="flex items-center gap-2 text-sm text-white/70"
            >
              <input
                type="checkbox"
                checked={packs.has(name)}
                disabled={!canEdit}
                data-testid="knowledge-pack-checkbox"
                data-pack={name}
                onChange={() =>
                  setPacks((prev) => {
                    const next = new Set(prev);
                    if (next.has(name)) next.delete(name);
                    else next.add(name);
                    return next;
                  })
                }
                className="accent-brand-500"
              />
              <span className="font-mono text-xs">{name}</span>
              {unknownPacks.includes(name) && (
                <span
                  className="text-[11px] text-amber-400"
                  data-testid="knowledge-unknown-pack"
                >
                  not a pack of this engagement — untick it
                </span>
              )}
              {name in rulesByPack && (
                <span className="text-[11px] text-white/30">
                  {rulesByPack[name]} rules
                </span>
              )}
            </label>
          ))}
        </div>
      )}
      {canEdit && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (recorded in the audit trail)"
            maxLength={2000}
            data-testid="knowledge-executable-reason"
            className="flex-1 min-w-[200px] px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white placeholder-white/20"
          />
          <button
            onClick={save}
            disabled={saving}
            data-testid="knowledge-executable-save"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
            style={{ background: "linear-gradient(135deg, #86BC25, #6B9B1E)" }}
          >
            {saving ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Save size={14} />
            )}
            Save
          </button>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Context knowledge — per-agent include / exclude
// ---------------------------------------------------------------------------

function ContextSection({
  view,
  projectId,
  onChanged,
  onError,
  onPromote,
}: {
  view: ProjectKnowledgeView;
  projectId: string;
  onChanged: () => Promise<void>;
  onError: (message: string) => void;
  onPromote: (item: ContextItem) => void;
}) {
  const canEdit = view.permissions.can_select_context;
  const [busy, setBusy] = useState<string | null>(null);

  const toggle = async (item: ContextItem, agentKey: string) => {
    const current = item.selection[agentKey];
    const mode: SelectionMode = current.included ? "exclude" : "include";
    setBusy(`${item.ref}:${agentKey}`);
    try {
      await knowledgeHub.setSelection(projectId, {
        agent_key: agentKey,
        item_ref: item.ref,
        mode,
      });
      await onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to change");
    } finally {
      setBusy(null);
    }
  };

  const reset = async (item: ContextItem, agentKey: string) => {
    setBusy(`${item.ref}:${agentKey}`);
    try {
      await knowledgeHub.setSelection(projectId, {
        agent_key: agentKey,
        item_ref: item.ref,
        mode: "default",
      });
      await onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to reset");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section data-testid="knowledge-context">
      <h4 className="text-xs font-bold text-white/70 uppercase tracking-wide mb-1">
        Context knowledge — documents per agent
      </h4>
      <p className="text-xs text-white/40 mb-3">
        Choose which documents each agent may use, to keep its context small.
        Choices are recorded now; an agent applies them once it reads through
        the Knowledge Hub (shown under its name). Token counts are estimates.
      </p>
      {view.context_items.length === 0 ? (
        <p
          className="text-xs text-white/25 italic"
          data-testid="knowledge-context-empty"
        >
          No documents yet. Upload them in Data Converter, Semantic Intelligence
          or Conversational AI, or ask an administrator to share tenant
          documents.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table
            className="w-full text-xs"
            data-testid="knowledge-context-table"
          >
            <thead>
              <tr className="text-white/40 text-left">
                <th className="py-1.5 pr-3 font-medium">Document</th>
                {view.agents.map((a) => (
                  <th
                    key={a.key}
                    className="py-1.5 px-2 font-medium text-center"
                  >
                    <div>{a.label}</div>
                    <div className="text-[10px] text-white/25 font-normal">
                      {a.reads_through_hub
                        ? "applied"
                        : `from phase ${a.reads_through_hub_from_phase ?? "—"}`}
                    </div>
                  </th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {view.context_items.map((item) => (
                <tr
                  key={item.ref}
                  className="border-t border-white/5"
                  data-testid="knowledge-context-row"
                  data-ref={item.ref}
                >
                  <td className="py-2 pr-3 align-top">
                    <div className="text-white/80">{item.title}</div>
                    <div className="text-[11px] text-white/30 flex flex-wrap gap-x-2">
                      <span>{VISIBILITY_LABELS[item.visibility]}</span>
                      <span>{STORE_LABELS[item.store] ?? item.store}</span>
                      <span>≈ {item.size_estimate_tokens} tokens</span>
                      {!item.llm_allowed && (
                        <span title="This document's text never goes to an LLM">
                          not sent to LLMs
                        </span>
                      )}
                    </div>
                  </td>
                  {view.agents.map((a) => {
                    const choice = item.selection[a.key];
                    const key = `${item.ref}:${a.key}`;
                    return (
                      <td key={a.key} className="py-2 px-2 text-center">
                        <input
                          type="checkbox"
                          checked={choice.included}
                          disabled={!canEdit || busy === key}
                          onChange={() => toggle(item, a.key)}
                          title={
                            choice.blocked_reason ??
                            (choice.source === "default"
                              ? "Default"
                              : `Set for the ${choice.source}`)
                          }
                          aria-label={`${a.label}: ${item.title}`}
                          data-testid="knowledge-context-checkbox"
                          data-agent={a.key}
                          className="accent-brand-500"
                        />
                        {choice.blocked_reason && choice.included && (
                          <div className="text-[10px] text-amber-400">
                            blocked
                          </div>
                        )}
                        {choice.source !== "default" && canEdit && (
                          <button
                            onClick={() => reset(item, a.key)}
                            className="block mx-auto text-[10px] text-white/30 hover:text-white/60"
                            data-testid="knowledge-context-reset"
                          >
                            reset
                          </button>
                        )}
                      </td>
                    );
                  })}
                  <td className="py-2 pl-2 align-top">
                    {view.permissions.can_promote &&
                      item.visibility === "project_private" &&
                      item.store !== "mdm" && (
                        <button
                          onClick={() => onPromote(item)}
                          className="flex items-center gap-1 text-[11px] text-white/40 hover:text-brand-400"
                          data-testid="knowledge-promote"
                          title="Copy to tenant-shared knowledge"
                        >
                          <Share2 size={12} /> Share
                        </button>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Per-agent context budgets
// ---------------------------------------------------------------------------

function BudgetSection({
  view,
  projectId,
  onSaved,
  onError,
}: {
  view: ProjectKnowledgeView;
  projectId: string;
  onSaved: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const canEdit = view.permissions.can_select_context;
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      view.agents.map((a) => [a.key, a.budget != null ? String(a.budget) : ""]),
    ),
  );

  const save = async (agentKey: string) => {
    const raw = (values[agentKey] ?? "").trim();
    const stored = view.agents.find((a) => a.key === agentKey)?.budget ?? null;
    if (raw !== "" && !/^\d+$/.test(raw)) {
      onError(
        "A budget is a whole number of tokens (or empty for the default).",
      );
      return;
    }
    const next = raw === "" ? null : Number.parseInt(raw, 10);
    if (next === stored) return; // unchanged: no request, no audit event
    try {
      await knowledgeHub.setBudget(projectId, agentKey, next);
      await onSaved();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to save budget");
    }
  };

  return (
    <section data-testid="knowledge-budgets">
      <h4 className="text-xs font-bold text-white/70 uppercase tracking-wide mb-1">
        Context budget per agent (≈ tokens)
      </h4>
      <p className="text-xs text-white/40 mb-3">
        The most knowledge context an agent carries per request. Empty = the
        default ({view.agents[0]?.default_budget ?? 2500}). Allowed:{" "}
        {view.limits.min_budget}–{view.limits.max_budget}.
      </p>
      <div className="grid gap-2 sm:grid-cols-3">
        {view.agents.map((a) => (
          <label
            key={a.key}
            className="text-xs text-white/50 flex items-center gap-2"
          >
            <span className="w-36 truncate">{a.label}</span>
            <input
              value={values[a.key] ?? ""}
              onChange={(e) =>
                setValues((v) => ({ ...v, [a.key]: e.target.value }))
              }
              onBlur={() => canEdit && save(a.key)}
              disabled={!canEdit}
              inputMode="numeric"
              placeholder={String(a.default_budget)}
              data-testid="knowledge-budget-input"
              data-agent={a.key}
              className="w-24 px-2 py-1 bg-surface-300/50 border border-white/8 rounded text-white disabled:opacity-50"
            />
          </label>
        ))}
      </div>
    </section>
  );
}

function ReferencePacks({ view }: { view: ProjectKnowledgeView }) {
  const [open, setOpen] = useState(false);
  return (
    <section data-testid="knowledge-reference-packs">
      <button
        onClick={() => setOpen((o) => !o)}
        className="text-xs font-bold text-white/70 uppercase tracking-wide flex items-center gap-2"
      >
        ZTD-provided reference packs ({view.reference_packs.length})
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {open && (
        <ul className="mt-2 space-y-1">
          {view.reference_packs.map((p) => (
            <li key={p.ref} className="text-xs text-white/50">
              <span className="font-mono text-white/70">{p.title}</span>{" "}
              {p.version && <span className="text-white/30">v{p.version}</span>}{" "}
              — {p.managed_by}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Warning({
  children,
  testId,
}: {
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <div
      className="mb-3 flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2"
      data-testid={testId}
    >
      <AlertTriangle size={13} className="mt-0.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}
